import React from 'react';
import { useTranslation } from 'react-i18next';
import { Heading4, spacing, TextParagraph } from '~/theme';
import Accordion, { AccordionPanel } from '~/components/common/accordion';
import { TextParagraphBold, UnorderedList } from '~/theme/elements';
import { urlWithBasePath } from '~/utils/url-helpers';
import Image from 'next/image';
import { defaultLocale } from '~/locale/defaults';
import { StyledBackButton } from '~/components/common/back-link/styles';
import sanitizeHtml from '~/utils/sanitize-html';
import InfoIcon from '~/assets/icons/ontario-icon-alert-information.svg';
import {
    AlertBody,
    AlertContainer,
    AlertHeadingContainer,
    AlertHeadingIcon,
} from '~/components/common/page-alerts/styles';
import { CardContainer } from './styles';
import { useRouter } from 'next/router';
import { styled } from 'styled-components';

const AlertHeading = styled(Heading4)`
    padding-top: calc(${spacing.spacing1} * 0.5);
`;
const StyledLinkButton = styled(StyledBackButton)`
    text-align: left;
`;
function PayFeeFaq() {
    const { t } = useTranslation();
    const router = useRouter();
    const config = {
        ADD_ATTR: ['target'],
        ALLOWED_ATTR: ['href', 'title', 'target'],
        ALLOWED_TAGS: ['a'],
        FORCE_BODY: true,
    };
    const ROUTES = { ADDRESS_CHANGE: `${router.locale ?? defaultLocale}/address-change` };
    const payFeeFaqPanelData: AccordionPanel[] = [
        {
            name: 'beforeYouStart',
            title: t('pay-fine:pay-fee.beforeYouStart.heading'),
            isOpen: true,
            content: (
                <>
                    <TextParagraphBold>
                        {t('pay-fine:pay-fee.beforeYouStart.info1')}
                    </TextParagraphBold>
                    <UnorderedList>
                        <li>{t('pay-fine:pay-fee.beforeYouStart.point1')}</li>
                        <li>{t('pay-fine:pay-fee.beforeYouStart.point2')}</li>
                        <li>{t('pay-fine:pay-fee.beforeYouStart.point3')}</li>
                        <li>{t('pay-fine:pay-fee.beforeYouStart.point4')} </li>
                    </UnorderedList>
                </>
            ),
        },
        {
            name: 'onlineRenewalEligibility',
            title: t('pay-fine:pay-fee.onlineRenewalEligibility.heading'),
            isOpen: true,
            content: (
                <>
                    <TextParagraph>
                        {t('pay-fine:pay-fee.onlineRenewalEligibility.info1')}
                    </TextParagraph>
                    <AlertContainer $alertType="informational">
                        <AlertHeadingContainer>
                            <AlertHeadingIcon>
                                <InfoIcon aria-hidden="true" focusable="false" />
                            </AlertHeadingIcon>

                            <AlertHeading $styledAs="h4" id="alertRef">
                                {t('pay-fine:pay-fee.onlineRenewalEligibility.pageAlertheading')}
                            </AlertHeading>
                        </AlertHeadingContainer>
                        <AlertBody>
                            <>
                                <TextParagraph
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(
                                            t(
                                                'pay-fine:pay-fee.onlineRenewalEligibility.pageDescription',
                                                {
                                                    addressChangeUrl: urlWithBasePath(
                                                        ROUTES.ADDRESS_CHANGE
                                                    ),
                                                }
                                            ) ?? '',
                                            { FORCE_BODY: true }
                                        ),
                                    }}
                                />
                                <TextParagraph
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(
                                            t(
                                                'pay-fine:pay-fee.onlineRenewalEligibility.addressDescription'
                                            ) ?? '',
                                            config
                                        ),
                                    }}
                                />
                            </>
                        </AlertBody>
                    </AlertContainer>
                </>
            ),
        },
        {
            name: 'Payment',
            title: t('pay-fine:pay-fee.payment.heading'),
            content: (
                <>
                    <TextParagraphBold>{t('pay-fine:pay-fee.payment.info1')}</TextParagraphBold>
                    <UnorderedList>
                        <li>
                            <CardContainer>
                                <TextParagraph>
                                    {' '}
                                    {t('pay-fine:pay-fee.payment.point1')}
                                </TextParagraph>
                                <Image
                                    src={urlWithBasePath('/images/visa-mastercard.png')}
                                    width={258}
                                    height={37}
                                    alt={t('pay-fine:pay-fee.payment.imageAltText')}
                                />{' '}
                            </CardContainer>
                        </li>
                    </UnorderedList>
                </>
            ),
        },
        {
            name: 'collectionOfPersonalInformation',
            title: t('pay-fine:pay-fee.information.heading'),
            content: (
                <>
                    <TextParagraph
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(t('pay-fine:pay-fee.information.point1') ?? '', {
                                FORCE_BODY: true,
                            }),
                        }}
                    />
                    {/* TBD: this is wrong, we should not use Styled component of Back Link component
                    directly, instead use the BackLink component */}
                    <StyledLinkButton
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(t('pay-fine:pay-fee.information.point2') ?? '', {
                                FORCE_BODY: true,
                            }),
                        }}
                    />
                </>
            ),
        },

        {
            name: 'privacyStatement',
            title: t('pay-fine:pay-fee.privacy.heading'),
            content: (
                <>
                    <TextParagraph>{t('pay-fine:pay-fee.privacy.point1')}</TextParagraph>
                    {/* TBD: this is wrong, we should not use Styled component of Back Link component
                    directly, instead use the BackLink component */}
                    <StyledLinkButton
                        dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(t('pay-fine:pay-fee.privacy.point2') ?? '', {
                                FORCE_BODY: true,
                            }),
                        }}
                    />
                </>
            ),
        },
    ];

    return (
        <>
            <Accordion showExpandAll panelData={payFeeFaqPanelData} />
        </>
    );
}

export default PayFeeFaq;
